import api from './axiosConfig';

export interface ToolPagedResult<T> {
  data: T[];
  total: number;
  pageNumber: number;
  pageSize: number;
}

export interface OrganizationOption {
  id: string;
  name: string;
}

export interface EmployeeOption {
  id: string;
  name: string;
  employeeCode?: string;
}

export interface AnnouncementItem {
  id: string;
  subject: string;
  sentBy: string;
  sentAt: string;
  attachmentFilePath?: string;
  attachmentFileName?: string;
}

export interface AnnouncementDetail extends AnnouncementItem {
  body: string;
  organizationIds: string[];
}

export interface ApprovalForwardSettingItem {
  id: string;
  effectiveFrom: string;
  effectiveTo: string;
  fromEmployeeId: string;
  fromEmployeeName: string;
  toEmployeeId: string;
  toEmployeeName: string;
  isActive: boolean;
  status: 'Active' | 'Inactive';
}

export interface SaveApprovalForwardSettingPayload {
  id?: string;
  effectiveFrom: string;
  effectiveTo: string;
  fromEmployeeId: string;
  toEmployeeId: string;
  isActive: boolean;
}

export const toolsApi = {
  getOrganizations: async (): Promise<OrganizationOption[]> => {
    const response = await api.get<OrganizationOption[]>('/tools/organizations');
    return response.data;
  },

  getEmployees: async (): Promise<EmployeeOption[]> => {
    const response = await api.get<EmployeeOption[]>('/tools/employees');
    return response.data;
  },

  getAnnouncements: async (pageNumber = 1, pageSize = 10, searchTerm?: string): Promise<ToolPagedResult<AnnouncementItem>> => {
    const response = await api.get<ToolPagedResult<AnnouncementItem>>('/tools/announcements', {
      params: { pageNumber, pageSize, searchTerm: searchTerm || undefined },
    });
    return response.data;
  },

  getAnnouncementById: async (id: string): Promise<AnnouncementDetail> => {
    const response = await api.get<AnnouncementDetail>(`/tools/announcements/${id}`);
    return response.data;
  },

  sendAnnouncement: async (payload: {
    subject: string;
    body: string;
    organizationIds: string[];
    document?: File;
  }): Promise<{ id: string; message: string; emailsSent: number; whatsAppsSent: number; totalRecipients: number }> => {
    const formData = new FormData();
    formData.append('subject', payload.subject);
    formData.append('body', payload.body);
    payload.organizationIds.forEach((id) => formData.append('organizationIds', id));
    if (payload.document) {
      formData.append('document', payload.document);
    }

    const response = await api.post<{ id: string; message: string; emailsSent: number; whatsAppsSent: number; totalRecipients: number }>('/tools/announcements', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getApprovalForwardSettings: async (params: {
    fromEmployeeId?: string;
    toEmployeeId?: string;
    searchTerm?: string;
    pageNumber?: number;
    pageSize?: number;
  }): Promise<ToolPagedResult<ApprovalForwardSettingItem>> => {
    const response = await api.get<ToolPagedResult<ApprovalForwardSettingItem>>('/tools/approval-forward-settings', { params });
    return response.data;
  },

  createApprovalForwardSetting: async (payload: SaveApprovalForwardSettingPayload): Promise<{ id: string; message: string }> => {
    const response = await api.post<{ id: string; message: string }>('/tools/approval-forward-settings', payload);
    return response.data;
  },

  updateApprovalForwardSetting: async (id: string, payload: SaveApprovalForwardSettingPayload): Promise<void> => {
    await api.put(`/tools/approval-forward-settings/${id}`, { ...payload, id });
  },
};
